import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, X, Save, Globe, Link as LinkIcon, Zap, MapPin, ClipboardList, Smartphone, FileText, MessageSquare, Receipt, Headset, Gift, GraduationCap, Car, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/I18nProvider";
import InstallAppButton from "@/components/InstallAppButton";
import ShareButton from "@/components/share/ShareButton";
import { siteBaseUrl } from "@/lib/seo";
import AccountDangerZone from "@/components/AccountDangerZone";
import AccountSecurityPanel from "@/components/AccountSecurityPanel";
import PrivacyCenter from "@/components/PrivacyCenter";
import useInstallPrompt from "@/hooks/useInstallPrompt";

const COMMON_CATEGORIES = ["Sedan", "Hatchback", "SUV", "Pickup", "Van", "Scooter", "Motorcycle", "Other"];

export default function Settings() {
  const { user, company: authCompany, refresh } = useAuth();
  const { t, lang, setLang, languages } = useT();
  const { installed } = useInstallPrompt();
  const [company, setCompany] = useState(authCompany);
  const [rates, setRates] = useState({});
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [newRate, setNewRate] = useState("");

  useEffect(() => {
    api.get("/company/settings").then((r) => {
      setCompany(r.data);
      setRates(r.data.default_rates_by_category || {});
    });
  }, []);

  const updateRate = (cat, value) => {
    setRates((r) => ({ ...r, [cat]: value }));
  };
  const removeRate = (cat) => {
    setRates((r) => {
      const out = { ...r };
      delete out[cat];
      return out;
    });
  };
  const addRate = () => {
    if (!newCat.trim() || !newRate) {
      toast.error("Category and rate are required");
      return;
    }
    if (rates[newCat]) {
      toast.error("Category already has a rate");
      return;
    }
    setRates((r) => ({ ...r, [newCat.trim()]: Number(newRate) }));
    setNewCat("");
    setNewRate("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = {};
      for (const [k, v] of Object.entries(rates)) {
        const n = Number(v);
        if (n > 0) cleaned[k] = n;
      }
      const { data } = await api.put("/company/settings", { default_rates_by_category: cleaned });
      setCompany(data);
      setRates(data.default_rates_by_category || {});
      toast.success("Settings saved");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const [savingDriverServices, setSavingDriverServices] = useState(false);
  const toggleDriverServices = async (next) => {
    setSavingDriverServices(true);
    try {
      const { data } = await api.put("/company/settings", { driver_services_enabled: next });
      setCompany(data);
      toast.success(next ? "Driver services enabled" : "Driver services disabled");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDriverServices(false);
    }
  };

  const unusedCategories = COMMON_CATEGORIES.filter((c) => !rates[c]);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">{t("settings.title")}</h1>
        <p className="text-slate-600 mt-1">Company info, day rates, and account details.</p>
      </div>

      {/* Language preference */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-language-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
            <Globe className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold">{t("settings.language.title")}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{t("settings.language.subtitle")}</p>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="settings-language-select"
              className="mt-3 w-full sm:max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#1B4332] focus:outline-none bg-white"
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.endonym}: {l.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-2">{t("lang.picker.beta")}</p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-share-website-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
            <Globe className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold">Share your public website</h2>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Send the canonical ConfirmPH link to renters through Messenger, WhatsApp, Viber or SMS: or copy it to paste anywhere.
            </p>

            {company?.public_slug && company?.public_website_enabled !== false ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <ShareButton
                  slug={company.public_slug}
                  businessName={company.name}
                  description={company.public_description_full || company.description || ""}
                  variant="primary"
                  size="md"
                  testId="settings-share-website-btn"
                  label="Share website"
                />
                <code
                  className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1 truncate max-w-full"
                  data-testid="settings-share-website-url"
                >
                  {`${siteBaseUrl()}/rentals/${company.public_slug}`}
                </code>
              </div>
            ) : (
              <div className="mt-3 inline-flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5"
                   data-testid="settings-share-website-locked">
                Activate your website before sharing.
              </div>
            )}
          </div>
        </div>
      </div>

      <Link
        to="/app/settings/website-integrations"
        className="block rounded-xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:shadow-sm transition"
        data-testid="settings-website-integrations-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
            <LinkIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold">Website &amp; Integrations</h2>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Connect your existing website, verify ownership, prepare a hosted booking page, and install the ConfirmPH widget.
            </p>
            <span className="inline-flex mt-3 text-sm font-medium text-emerald-700">Manage integrations →</span>
          </div>
        </div>
      </Link>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-install-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold">Install ConfirmPH as an app</h2>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Add ConfirmPH to your phone&apos;s home screen for one-tap access to bookings, vehicles and customer requests: works offline for previously-viewed pages.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {installed ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium" data-testid="settings-install-already">
                  ✓ Installed on this device
                </span>
              ) : (
                <InstallAppButton
                  variant="primary"
                  size="md"
                  testId="settings-install-app-btn"
                  label="Install ConfirmPH"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-in-app-bookings-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
            <Zap className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-lg font-semibold">In-app bookings</h2>
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 uppercase tracking-wider">Pro / Enterprise</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Let customers request or hold a booking directly through ConfirmPH, based on your fleet&apos;s availability.
              Stay in control: <b>your staff approves every request</b> by default.
            </p>
            <Link
              to="/app/settings/in-app-bookings"
              data-testid="settings-open-in-app-link"
              className="mt-3 inline-flex items-center gap-1 text-sm text-[#1B4332] hover:underline font-medium"
            >
              Configure in-app bookings →
            </Link>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-driver-services-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0">
            <Car className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold">Driver services</h2>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Turn this on if you offer rentals with a company driver. Off by default: self-drive-only
              businesses won&apos;t see driver setup, assignment, or pricing anywhere in the app.
              Turning this off later hides driver features again but never deletes existing driver data.
            </p>
            <label className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                data-testid="settings-driver-services-toggle"
                checked={!!company?.driver_services_enabled}
                disabled={savingDriverServices}
                onChange={(e) => toggleDriverServices(e.target.checked)}
              />
              Do you offer rentals with a driver?
            </label>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-3 text-sm">
        <h2 className="font-display text-lg font-semibold">Company</h2>
        <Row label="Name" value={company?.name} />
        <Row label="Business type" value={company?.business_type} />
        <Row label="City" value={company?.city || "-"} />
        <Row label="Email" value={company?.email} />
        <Row label="Phone" value={company?.phone || "-"} />
        <Row label="Status" value={company?.status} />
        <Row label="Buffer hours (between bookings)" value={`${company?.buffer_hours || 4} h`} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="default-rates-card">
        <div>
          <h2 className="font-display text-lg font-semibold">Default day rates by category</h2>
          <p className="text-sm text-slate-600 mt-1">
            When you add a vehicle, its <b>Price per day</b> will pre-fill from the rate of its category.
            You can always override it on the individual vehicle.
          </p>
        </div>

        <div className="space-y-2">
          {Object.keys(rates).length === 0 && (
            <p className="text-sm text-slate-500" data-testid="no-rates-msg">No category rates yet: add one below.</p>
          )}
          {Object.entries(rates).sort(([a], [b]) => a.localeCompare(b)).map(([cat, val]) => (
            <div key={cat} className="flex items-center gap-2" data-testid={`rate-row-${cat}`}>
              <span className="w-32 text-sm font-medium text-slate-800">{cat}</span>
              <span className="text-slate-500">₱</span>
              <input
                type="number"
                min={0}
                step={50}
                value={val}
                onChange={(e) => updateRate(cat, e.target.value)}
                data-testid={`rate-input-${cat}`}
                className="w-32 px-3 py-1.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none text-sm"
              />
              <span className="text-xs text-slate-500">/ day</span>
              <button
                type="button"
                onClick={() => removeRate(cat)}
                data-testid={`rate-remove-${cat}`}
                className="ml-auto p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                title="Remove this category rate"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Add a rate</div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              list="category-suggestions"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              placeholder="Category (e.g. SUV)"
              data-testid="new-rate-category"
              className="w-44 px-3 py-1.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none text-sm"
            />
            <datalist id="category-suggestions">
              {unusedCategories.map((c) => <option key={c} value={c} />)}
            </datalist>
            <span className="text-slate-500">₱</span>
            <input
              type="number"
              min={0}
              step={50}
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
              placeholder="2500"
              data-testid="new-rate-amount"
              className="w-32 px-3 py-1.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none text-sm"
            />
            <span className="text-xs text-slate-500">/ day</span>
            <button
              type="button"
              onClick={addRate}
              data-testid="new-rate-add"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="default-rates-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save rates"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-3 text-sm">
        <h2 className="font-display text-lg font-semibold">Your account</h2>
        <Row label="Name" value={user?.name} />
        <Row label="Email" value={user?.email} />
        <Row label="Role" value={user?.role} />
      </div>

      <AccountSecurityPanel />


      {/* Quick Links */}
      <div className="border-t pt-6">
        <p className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-3">QUICK LINKS</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/app/settings/public-profile"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <Globe className="w-4 h-4" /> Public Profile
          </Link>
          <Link to="/app/settings/public-website"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <LinkIcon className="w-4 h-4" /> Business Mini-Site
          </Link>
          <Link to="/app/settings/location"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <MapPin className="w-4 h-4" /> Business Location
          </Link>
          <Link to="/app/settings/in-app-bookings"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <Zap className="w-4 h-4" /> In-App Bookings
          </Link>
          <Link to="/app/settings/checklists"
                data-testid="settings-link-checklists"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <ClipboardList className="w-4 h-4" /> Inspection Checklists
          </Link>
          <Link to="/app/settings/terms"
                data-testid="settings-link-terms"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <FileText className="w-4 h-4" /> Booking Terms
          </Link>
          <Link to="/app/settings/communications"
                data-testid="settings-link-communications"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <MessageSquare className="w-4 h-4" /> Message Templates &amp; Automation
          </Link>
          <Link to="/app/settings/enhanced-safety"
                data-testid="settings-link-enhanced-safety"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <ShieldCheck className="w-4 h-4" /> Enhanced Vehicle Safety Enforcement
          </Link>
          <Link to="/app/billing"
                data-testid="settings-link-billing"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <Receipt className="w-4 h-4" /> Plans &amp; Billing
          </Link>
          <Link to="/app/help"
                data-testid="settings-link-support"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <Headset className="w-4 h-4" /> Help &amp; Support
          </Link>
          <Link to="/app/support"
                data-testid="settings-link-support-legacy"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <Headset className="w-4 h-4" /> Support Ticket History (legacy)
          </Link>
          <Link to="/app/partner-rewards"
                data-testid="settings-link-partner-rewards"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <Gift className="w-4 h-4" /> Partner Rewards
          </Link>
          <Link to="/app/onboarding"
                data-testid="settings-link-training"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 hover:border-emerald-600 text-sm">
            <GraduationCap className="w-4 h-4" /> Staff Training
          </Link>
        </div>
      </div>


      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600">
        <h3 className="font-display text-base font-semibold text-slate-900">Planned</h3>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>Custom logo &amp; branding</li>
          <li>Google Sheets &amp; Calendar import</li>
        </ul>
      </div>

      <PrivacyCenter context="business" />
      <AccountDangerZone />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}
